import Axios, { type CancelTokenSource } from 'axios';
import type ItineraryPackage from './ItineraryPackage';
import { type FilterData, type MetaData } from './ItineraryModuleTypes';
import { type ItineraryPackageLoaderConfig } from './types';
import { viewListMaxTime } from '@store/itinerary';
import { RequestCanceledException } from '@utils/itinerary';

const { CancelToken } = Axios;
let source: CancelTokenSource;

/**
 * The result from getPackages method of ItineraryPackageLoader
 */
export abstract class ItineraryPackageLoaderResult<
    TPackage extends ItineraryPackage,
> {
    abstract list: TPackage[];
    abstract filterMetaData: FilterData;
    abstract metaData?: MetaData;
}

/**
 * The base class for itinerary package loaders
 */
export default abstract class ItineraryPackageLoader<
    TPackage extends ItineraryPackage,
> {
    protected _url?: string;
    protected constructor(config: ItineraryPackageLoaderConfig) {
        this._url = config.url;
    }

    get isRealTime() {
        return false;
    }

    /**
     * url of loader
     *
     * @readonly
     * @memberof ItineraryPackageLoader
     */
    get url() {
        return this._url;
    }

    /**
     * set last loaded packages to load again
     *
     * @type {(Date | null)}
     * @memberof ItineraryPackageLoader
     */
    protected lastLoadTime: Date | null = null;

    /**
     * reset last loaded time to load again packages
     *
     * @memberof ItineraryPackageLoader
     */
    resetLoadTime() {
        this.lastLoadTime = null;
    }

    // returns `true` if this loader is equal to loader that has `other` configs
    abstract compareTo(other: ItineraryPackageLoaderConfig): boolean;

    /**
     * Loads all itinerary packages from the service
     * @param axiosParams Parameters to be passed to Axios
     */
    abstract _getPackages(
        axiosParams?: any
    ): AsyncGenerator<ItineraryPackageLoaderResult<TPackage>>;

    /**
     * get date of this loader
     *
     * @readonly
     * @abstract
     * @type {Date}
     * @memberof ItineraryPackageLoader
     */
    abstract get date(): Date;

    get needsLoad(): boolean {
        return (
            !this.lastLoadTime ||
            dayConfig.gregory().diff(this.lastLoadTime) > viewListMaxTime
        );
    }

    get needsReload() {
        if (!this.lastLoadTime) {
            return false;
        }
        return dayConfig.gregory().diff(this.lastLoadTime) > viewListMaxTime;
    }

    load() {
        if (!this.needsLoad) {
            return undefined;
        }
        return this.getPackages({
            timeout: 100000,
        });
    }

    /**
     * Loads all itinerary packages, and also checks for expiration time of the results
     * @param axiosParams The params that is passed to axios for its configuration
     */
    async *getPackages(
        axiosParams?: any
    ): AsyncGenerator<ItineraryPackageLoaderResult<TPackage>> {
        if (!this.needsLoad) {
            const exception = new RequestCanceledException();
            exception.requestWasCanceled = false;
            exception.requestAlreadyLoaded = true;
            throw exception;
        }

        source = CancelToken.source();

        const packages = this._getPackages({
            ...axiosParams,
            cancelToken: source.token,
        });
        for await (const pkg of packages) {
            yield pkg;
        }
        this.lastLoadTime = new Date();
    }

    stopLoading(): void {
        // Checking for any previous requests and cancel them
        if (source) {
            source.cancel('canceled');
        }
    }
}
